import React from 'react';
import {
  Link
} from "react-router-dom";
import {
  Accordion,
  Button,
  ButtonGroup, 
  ButtonToolbar,
  Card,
  Col,
  Container,
  ListGroup,
  Row,
  Table
} from 'react-bootstrap';

import useUser from '../hooks/useUser';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import * as formatter from '../helpers/formatting';

export default function Invoice(data) {
  const compactDisplay = !!data.compact;
  const listDisplay = !!data.listItem;
  const showHistory = !!data.showHistory;

  return (
      <Card className="text-start">
        <Card.Header>
          <Container fluid className="g-0">
            <Row>
              <Col>
                Invoice { data.invoiceData.external_id || data.invoiceData.ro_number || data.invoiceData.id }
              </Col>
              <Col xs={4} className="text-end">
                { !compactDisplay && 
                  <InvoiceStatusBadge invoiceData={ data.invoiceData } />
                }              
              </Col>
            </Row>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container fluid className="g-0">
            <Row>
              <Col s={6}>
                <Card.Title><h3>{ formatter.formatCurrency(data.invoiceData.amount) }</h3></Card.Title>
              </Col>
              <Col s={6} className="text-end text-secondary">
                { formatter.formatDateTime(data.invoiceData.sent_at) }
              </Col>
            </Row>
          </Container>
          { compactDisplay &&
            <Accordion className="invoiceAccordion">
              <Accordion.Item eventKey="0">
                <Accordion.Header>view details</Accordion.Header>
                <Accordion.Body>
                  <InvoiceBody 
                    showHistory={showHistory} 
                    invoiceData={data.invoiceData}
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          }     
          { listDisplay &&
            <InvoiceSummary invoiceData={data.invoiceData} />
          }               
          { !compactDisplay && !listDisplay &&
            <InvoiceBody 
              invoiceData={data.invoiceData} 
              showHistory={showHistory} 
            />
          }     
        </Card.Body>
      </Card>
  )
}


function InvoiceSummary(data) {
  const { user } = useUser();
  const userType = user.is_shop ? 'merchant' : 'customer';

  return (
          <Container fluid className="g-0">
            <Row>
              { user.user_type === "customer" &&
                <Col xs={12} sm={6}>
                  <strong>{ data.invoiceData.shop_name }</strong>
                </Col>
              }
              { userType === "merchant" &&
                <Col xs={12} sm={6}>
                  <strong>{ data.invoiceData.customer_name }</strong>
                </Col>
              }
              <Col xs={12} sm={6} className="text-sm-end">
                { data.invoiceData.vehicle_formatted }
              </Col>
            </Row>
            <Row>
              <Col className="mt-3">
                <Link to={`/${userType}/invoices/${ data.invoiceData.id }`}>
                  <Button className="float-end" variant="primary">View Invoice</Button>
                </Link>
              </Col>
            </Row>
          </Container>
  );
}

function InvoiceBody(data) {
  const { user } = useUser();
  const userType = user.is_shop ? 'merchant' : 'customer';  
  const showHistory = data.showHistory ? true : false;
  const shop_name = data.invoiceData.shop ? data.invoiceData.shop.data.name : '';

  return (

          <Container fluid className="g-0">
{/*
  Customer will need
  issuing shop shown here
*/}            
            { user.is_shop === false &&
              <Row className="mt-4">
                <Col xs={12}>
                  <h4>{ shop_name }</h4>
                </Col>
              </Row>
            }
            <Row className="mt-4">
              <Col xs={12}>
                { data.invoiceData.customer_name }<br/>
                { formatter.formatPhone(data.invoiceData.customer_phone) }<br/>
                { data.invoiceData.customer_email }
              </Col>
            </Row>
            <Row className="mt-4">
              <Col xs={12}>
                <strong>{ data.invoiceData.vehicle }</strong>
                <ListGroup variant="flush"
                   dangerouslySetInnerHTML={{__html:formatter.formatNewlines(data.invoiceData.notes, 'ListGroup.Item')}}
                />
              </Col>
            </Row>
            { showHistory && user.is_shop === false &&
              <PaymentHistory
                invoiceData={data.invoiceData} 
              />
            }
            { showHistory && user.is_shop === true &&
              <DisbursementHistory
                invoiceData={data.invoiceData} 
              />
            }           
            { showHistory &&
              <InvoiceHistory 
                invoiceData={data.invoiceData} 
              />
            }              
          </Container>
  );
}

function PaymentHistory(data) {
  const payments = data.invoiceData.payments ? data.invoiceData.payments.data : [];
  const list = payments ? payments.map((p,i) => {
    return (
      <tr key={i}>
        <td>{ formatter.formatCurrency(p.amount) }</td>
        <td className="text-end">{ formatter.formatDateTime(p.created_at) }</td>
      </tr>
    );
  }) : null;     

  return (  
    <Row className="mt-5">
      <Col xs={12} sm={6}>
        <strong>Payment History</strong>
        <p>{ payments.length } of 8 payments complete</p>
        { list && 
          <Table>
            <thead>
              <tr>
                <th>Amount</th>
                <th className="text-end">Date</th>
              </tr>
            </thead>
            <tbody>
              { list }
            </tbody>
          </Table> 
        }
        { !list && 
          <p>
            No payments recorded
          </p>
        }
        <p className="text-center">Next payment { formatter.formatDateTime(data.invoiceData.next_payment) }</p>
      </Col>
      <Col />
    </Row>  
  );              
}


function DisbursementHistory(data) {
  const disbursements = data.invoiceData.disbursements ? data.invoiceData.disbursements.data : [];
  const list = disbursements ? disbursements.map((d,i) => {
    return (
      <tr key={i}>
        <td>{ formatter.formatCurrency(d.amount) }</td>
        <td className="text-end">{ formatter.formatDateTime(data.invoiceData.paid_at) }</td>
      </tr>
    );
  }) : null;

  return (  
    <Row className="mt-5">
      <Col xs={12} sm={6}>
        <strong>Payment History</strong>
        { list && 
          <Table>
            <thead>
              <tr>
                <th>Amount</th>
                <th className="text-end">Date</th>
              </tr>
            </thead>
            <tbody>
              { list }
            </tbody>
          </Table> 
        }
        { !list && 
          <p>
            Payment pending
          </p>
        }
      </Col>
      <Col />
    </Row>  
  );              
}

function InvoiceHistory(data) {
  return (  
    <Row className="mt-5">
      <Col xs={12} sm={6}>
        <strong>Invoice History</strong>
        <Table>
          <thead>
            <tr>
              <th>Status</th>
              <th className="text-end">Date</th>
            </tr>
          </thead>
          <tbody>
            { data.invoiceData.refunded_at &&
              <tr>
                <td>Refunded</td>
                <td className="text-end">{ formatter.formatDateTime(data.invoiceData.refunded_at) }</td>
              </tr>
            }
            { data.invoiceData.canceled_at &&
              <tr>
                <td>Canceled</td>
                <td className="text-end">{ formatter.formatDateTime(data.invoiceData.canceled_at) }</td>
              </tr>
            }
            { data.invoiceData.paid_at &&
              <tr>
                <td>Paid to Shop</td>
                <td className="text-end">{ formatter.formatDateTime(data.invoiceData.paid_at) }</td>
              </tr>
            }
            <tr>
              <td>Sent</td>
              <td className="text-end">{ formatter.formatDateTime(data.invoiceData.sent_at) }</td>
            </tr>
          </tbody>
        </Table>  
      </Col>
      <Col />
    </Row>  
  );              
}